"use client";

import {
  ChartBarIcon,
  CodeBracketIcon,
  CreditCardIcon,
  LinkIcon,
  SwatchIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import Link from "next/link";
import { ComponentPropsWithoutRef, FC, useState } from "react";
import { PRESET_IN_APP_NAME } from "../const/presetConst";
import { PLAYGROUND_PROJECT_ID } from "../const/projectConts";
import { useIsMarketingPage } from "../context/marketingPageContext";
import { useProfile } from "../hooks/useProfile";
import Avatar from "./Avatar";
import CalendarNavLink from "./autoPublish/CalendarNavLink";
import BarsMenu from "./BarsMenu";
import Button from "./Button";
import DropdownMenu from "./DropdownMenu";
import { SHOW_TIMELINE_PARAM } from "./HideTimelineButton";
import LoginButton from "./LoginButton";
import NewsMenu from "./NewsMenu";
import { useSessionContext } from "./SupabaseProvider";

type UserNavProps = {} & ComponentPropsWithoutRef<"div">;

const UserNav: FC<UserNavProps> = ({ ...rest }) => {
  const [open, setOpen] = useState(false);
  const { data: profile } = useProfile();
  const { session, isLoading } = useSessionContext();
  const isMarketingPage = useIsMarketingPage();

  const myTemplatesHref = `/editor/${PLAYGROUND_PROJECT_ID}?mode=captions/style&${SHOW_TIMELINE_PARAM}=0`;

  const buildButtonsListItems = () => {
    return (
      <>
        <li>
          <Link className="truncate" href={"/spaces"}>
            <Button
              size="m"
              variant="tertiary"
              onClick={() => {}}
              className="w-full truncate"
              align="start"
            >
              Videos
            </Button>
          </Link>
        </li>
        <li>
          <CalendarNavLink fullWidth />
        </li>
        <li>
          <Link className="truncate" href={"/analytics"}>
            <Button
              size="m"
              variant="tertiary"
              onClick={() => {}}
              className="w-full truncate"
              align="start"
            >
              Analytics
            </Button>
          </Link>
        </li>
      </>
    );
  };

  // Marketing pages always render `<LoginButton />` regardless of session
  // state — there it shows the "Log in / Get started" pair, or "Dashboard"
  // once a session resolves (see `marketingPageContext`). So we can render it
  // optimistically during `isLoading` and avoid the empty-then-populated
  // flash that the previous `if (isLoading) return null` produced.
  if (isMarketingPage) {
    return (
      <div {...rest} className={`${rest.className} flex items-end`}>
        <LoginButton />
      </div>
    );
  }

  // For authenticated routes we still wait for Supabase to resolve before
  // deciding between the avatar dropdown and the login button — the alternative
  // (rendering one and swapping on resolve) would itself flash.
  if (isLoading) return null;

  return (
    <div {...rest} className={`${rest.className} flex items-end`}>
      {session ? (
        <div className="flex gap-2 truncate">
          <BarsMenu className="md:hidden">
            <ul className="gap-2 flex flex-col">{buildButtonsListItems()}</ul>
          </BarsMenu>
          <NewsMenu />
          <DropdownMenu
            containerClassName="w-48"
            clickOutsideCapture
            onClickOutside={() => {
              setOpen(false);
            }}
            positions={["bottom"]}
            align="end"
            open={open}
            onOpenChange={(next) => setOpen(next)}
          >
            <DropdownMenu.Trigger>
              <Button variant="tertiary" size="m" className="!p-1">
                <Button.Content>
                  <Avatar className="w-7 h-7" src={profile?.avatarUrl} />
                </Button.Content>
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {({ close }) => (
                <>
                  <Link
                    className="overflow-ellipsis overflow-hidden"
                    href={"/account"}
                  >
                    <Button
                      size="m"
                      variant="tertiary"
                      onClick={close}
                      className="w-full flex hover:!bg-slate-100"
                      align="start"
                    >
                      <UserIcon /> Account
                    </Button>
                  </Link>
                  <Link
                    className="overflow-ellipsis overflow-hidden"
                    href={"/social-accounts"}
                  >
                    <Button
                      size="m"
                      variant="tertiary"
                      onClick={close}
                      className="w-full flex hover:!bg-slate-100"
                      align="start"
                    >
                      <LinkIcon /> Social accounts
                    </Button>
                  </Link>
                  <Link
                    className="overflow-ellipsis overflow-hidden"
                    href={myTemplatesHref}
                  >
                    <Button
                      size="m"
                      variant="tertiary"
                      onClick={close}
                      className="w-full flex hover:!bg-slate-100 capitalize"
                      align="start"
                    >
                      <SwatchIcon />
                      {`${PRESET_IN_APP_NAME}s`}
                    </Button>
                  </Link>
                  <Link
                    className="overflow-ellipsis overflow-hidden"
                    href={"/usage"}
                  >
                    <Button
                      size="m"
                      variant="tertiary"
                      onClick={close}
                      className="w-full flex hover:!bg-slate-100"
                      align="start"
                    >
                      <ChartBarIcon /> Usage
                    </Button>
                  </Link>
                  <Link
                    className="overflow-ellipsis overflow-hidden"
                    href={"/billing"}
                  >
                    <Button
                      size="m"
                      variant="tertiary"
                      onClick={close}
                      className="w-full flex hover:!bg-slate-100"
                      align="start"
                    >
                      <CreditCardIcon /> Billing
                    </Button>
                  </Link>
                  <Link
                    className="overflow-ellipsis overflow-hidden"
                    href={"/rest-api"}
                  >
                    <Button
                      size="m"
                      variant="tertiary"
                      onClick={close}
                      className="w-full flex hover:!bg-slate-100"
                      align="start"
                    >
                      <CodeBracketIcon /> API
                    </Button>
                  </Link>
                  <LoginButton />
                </>
              )}
            </DropdownMenu.Content>
          </DropdownMenu>
        </div>
      ) : (
        <LoginButton />
      )}
    </div>
  );
};

export default UserNav;
